(() => {
  const q = (root, selector) => root?.querySelector(selector);
  const qa = (root, selector) => [...(root?.querySelectorAll(selector) || [])];
  const uniq = (items) => [...new Set(items.filter(Boolean))];
  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  // Customer-facing order must not depend on the supplier's Shopify option
  // order or on composite raw values such as "Green 3PC".
  const FACET_ORDER = ['color', 'variant', 'quantity', 'set', 'size'];
  const FACET_HEADINGS = {
    color: 'COLOR',
    variant: 'VARIANT',
    quantity: 'QUANTITY',
    set: 'SET',
    size: 'SIZE'
  };

  const ELECTRIC_LICE_COMB_HANDLE = 'electric-lice-remover';
  const DESHEDDING_MITT_HANDLE = 'pet-hair-remover-mitt-pet-hair-remover-gloves-deshedding-brush-glove-for-dog-cat-rabbit-with-long-short-curly-hair';

  const colorMap = {
    'hot pink': '#ec4899', 'light blue': '#72bde8', 'sky blue': '#72bde8', 'dark blue': '#1e4f91',
    'navy blue': '#0d274f', 'rose red': '#d94f70', 'dark brown': '#6f4c35', 'light brown': '#bc906c',
    'multi color': 'conic-gradient(#2d7dd2, #ffd91a, #ec4899, #2d7dd2)',
    multicolor: 'conic-gradient(#2d7dd2, #ffd91a, #ec4899, #2d7dd2)',
    transparent: 'linear-gradient(135deg, #fff 45%, #8ecae6 46%, #8ecae6 54%, #fff 55%)',
    white: '#ffffff', black: '#111827', gray: '#9ca3af', grey: '#9ca3af', silver: '#cfd5dd',
    blue: '#3b82f6', navy: '#0d274f', green: '#5d9c76', red: '#d64545', pink: '#ef8fb3',
    yellow: '#f4c914', orange: '#f28c32', purple: '#8067c9', violet: '#8067c9', brown: '#8b5e3c',
    coffee: '#7c5b3b', beige: '#d6c2a1', khaki: '#b4a47b', mint: '#7bc6a7', lime: '#90c94f',
    gold: '#d5ad43', cream: '#eee3ce', rose: '#dd7898', burgundy: '#7f1d3a', maroon: '#7a263a',
    teal: '#2b8a86', turquoise: '#3dbdb5', aqua: '#55c7d8'
  };
  const colorNames = Object.keys(colorMap).sort((left, right) => right.length - left.length);

  const smartTitleCase = (value) => String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      const special = {
        xs: 'XS', s: 'S', m: 'M', l: 'L', xl: 'XL', xxl: '2XL', xxxl: '3XL',
        usb: 'USB', led: 'LED', pc: 'PC', pcs: 'PCS'
      };
      if (special[lower]) return special[lower];
      if (/^\d+xl$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

  const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const colorTokenFor = (value) => {
    const normalized = normalize(value);
    return colorNames.find((color) => new RegExp(`(?:^| )${escapeRegExp(color)}(?: |$)`).test(normalized)) || '';
  };

  const displayColor = (token) => smartTitleCase(token === 'grey' ? 'gray' : token);

  const sizeLabel = (value) => {
    const text = normalize(value);
    const aliases = {
      'extra small': 'XS', xs: 'XS', small: 'S', s: 'S', medium: 'M', m: 'M', large: 'L', l: 'L',
      'extra large': 'XL', xlarge: 'XL', xl: 'XL', xxl: '2XL', '2xl': '2XL',
      xxxl: '3XL', '3xl': '3XL', xxxxl: '4XL', '4xl': '4XL'
    };
    if (aliases[text]) return aliases[text];
    const leading = text.match(/^(xs|s|m|l|xl|xxl|2xl|xxxl|3xl|4xl)(?:\s|$)/)?.[1];
    return leading ? aliases[leading] : '';
  };

  const packInfo = (value) => {
    const text = String(value || '');
    const match = text.match(/(?:^|\s)(?:x|×)\s*(\d+)(?:$|\s)/i)
      || text.match(/(?:^|\s)(\d+)\s*(?:pcs?|pieces?|packs?|pairs?)(?:$|\s)/i)
      || text.match(/pack\s*of\s*(\d+)/i);
    if (!match) return { quantity: '', label: '', shortLabel: '', stripped: text.trim() };
    const quantity = Number(match[1]) || 1;
    return {
      quantity: String(quantity),
      label: quantity === 1 ? '1 Piece' : `${quantity}-Piece Pack`,
      shortLabel: quantity === 1 ? '1 PC' : `${quantity} PCS`,
      stripped: text.replace(match[0], ' ').replace(/[()•·]/g, ' ').replace(/\s+/g, ' ').trim()
    };
  };

  const inferGroupRole = (group, radios) => {
    const label = normalize(group.dataset.optionName || q(group, 'legend')?.textContent || '');
    const values = radios.map((radio) => radio.value);
    if (/\b(size|fit|measurement|format)\b/.test(label)
      || (values.length && values.every((value) => sizeLabel(value)))) return 'size';
    if (/\b(qty|quantity|pack|pieces|pcs)\b/.test(label)) return 'quantity';
    if (/\b(set|bundle)\b/.test(label)) return 'set';
    if (/\b(character|style|design|model|type|bowl|brush|product|fish|accessory)\b/.test(label)) return 'variant';
    if (/\b(colou?r|shade)\b/.test(label)) return 'color';
    if (/\bspecification\b/.test(label)) return values.some((value) => sizeLabel(value)) ? 'size' : 'variant';
    return 'variant';
  };

  const isSetValue = (value, role = '') => {
    const text = normalize(value);
    return role === 'set' || /^(?:set|bundle)(?:\s|\d|$)/.test(text) || /\b(?:set|bundle)$/.test(text);
  };

  const shouldExtractColors = (radios, role = 'variant') => {
    const parsed = radios.map((radio) => {
      const raw = String(radio.value || '');
      const pack = packInfo(raw);
      return { raw, pack, color: colorTokenFor(pack.stripped) };
    });
    const tokens = parsed.map((item) => item.color).filter(Boolean);
    if (!tokens.length) return false;
    const allColorChoices = tokens.length === radios.length;
    const nonColorsAreSetsOrQuantities = parsed.filter((item) => !item.color).every((item) => (
      isSetValue(item.raw, role) || (item.pack.label && !item.pack.stripped)
    ));
    if (role === 'variant' || role === 'product') return allColorChoices;
    // Shopify suppliers frequently mix a real non-color alternative (for
    // example Hair Removal Gloves) into an option named Color. Keep the actual
    // colors in COLOR and route that alternative to VARIANT.
    if (role === 'color') return uniq(tokens).length >= 1;
    return uniq(tokens).length >= 2 || allColorChoices || nonColorsAreSetsOrQuantities;
  };

  const formatSet = (rawValue, renderedValue) => {
    const raw = String(rawValue || '').trim();
    const rendered = String(renderedValue || '').trim();
    const match = raw.match(/^(?:set|bundle)\s*([a-z0-9-]*)$/i);
    if (!match) return rendered || smartTitleCase(raw);
    const suffix = smartTitleCase(match[1] || '');
    return suffix ? `Set ${suffix}` : 'Set';
  };

  const formatGenericVariant = (rawValue, renderedValue, curated) => {
    if (curated && renderedValue) return renderedValue;
    const raw = String(rawValue || '').trim();
    const leading = raw.match(/^(\d+)\s*style$/i);
    const trailing = raw.match(/^(?:style|variant|type)\s*([a-z0-9-]+)$/i);
    const token = leading?.[1] || trailing?.[1];
    return token ? `Design ${smartTitleCase(token)}` : (renderedValue || smartTitleCase(raw));
  };

  const quantityForRole = (original, role, parsedPack) => {
    if (parsedPack.shortLabel) return parsedPack.shortLabel;
    if (role !== 'quantity') return '';
    const numeric = normalize(original).match(/^\d+$/)?.[0];
    if (!numeric) return '';
    return Number(numeric) === 1 ? '1 PC' : `${Number(numeric)} PCS`;
  };

  const removeColorToken = (value, token) => {
    if (!token) return String(value || '').trim();
    return normalize(value)
      .replace(new RegExp(`(?:^| )${escapeRegExp(token)}(?: |$)`), ' ')
      .replace(/\b[a-z]{1,4}\d{2,}\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const parseItem = (radio, role, colorMode, quantityMode = false) => {
    const original = String(radio.value || '').replace(/\s+/g, ' ').trim();
    const rendered = String(radio.dataset.displayValue || '').replace(/\s+/g, ' ').trim();
    const curated = radio.dataset.fvCuratedLabel === 'true'
      && rendered
      && normalize(rendered) !== normalize(original);
    const display = rendered || smartTitleCase(original);
    const normalizedSize = role === 'size'
      ? (sizeLabel(original) || sizeLabel(display) || smartTitleCase(display))
      : '';
    if (normalizedSize) {
      return {
        radio, original, display: normalizedSize, family: 'size', color: '', variant: '', quantity: '',
        set: '', size: normalizedSize, product: '', accessory: '', pack: ''
      };
    }

    if (isSetValue(original, role)) {
      const set = formatSet(original, curated ? display : '');
      return {
        radio, original, display: set, family: 'set', color: '', variant: '', quantity: '', set,
        size: '', product: '', accessory: '', pack: ''
      };
    }

    const parsedPack = packInfo(original);
    const quantity = quantityForRole(original, role, parsedPack)
      || (quantityMode && parsedPack.stripped ? '1 PC' : '');
    const rawColor = colorMode ? colorTokenFor(parsedPack.stripped) : '';
    const color = rawColor ? displayColor(rawColor) : '';
    let variant = removeColorToken(parsedPack.stripped, rawColor);
    if (role === 'color' && color && !variant) variant = '';
    else if (role === 'quantity' && (parsedPack.shortLabel || /^\d+$/.test(normalize(original)))) variant = '';
    else if (curated && !rawColor) variant = display.split(/\s*[·•]\s*/)[0].trim();
    else variant = formatGenericVariant(variant || original, variant ? smartTitleCase(variant) : display, false);
    if (normalize(variant) === 'as shown') variant = '';

    const accessory = /\b(cable|charger|adapter|accessory)\b/i.test(original) ? (curated ? display : smartTitleCase(original)) : '';
    if (accessory) variant = accessory;
    return {
      radio, original, display, family: 'choice', color, variant, quantity: accessory ? '' : quantity,
      set: '', size: '', product: accessory ? '' : variant, accessory,
      pack: accessory ? '' : (parsedPack.label || (quantity === '1 PC' ? '1 Piece' : ''))
    };
  };

  const applyProductSemantics = (root, model) => {
    const handle = root.dataset.productHandle || '';
    if (handle === ELECTRIC_LICE_COMB_HANDLE && model.position === 0) {
      model.items.forEach((item) => {
        // "White" is a supplier color entry for the separate manual-looking
        // product. Keep its native radio/Shopify variant intact, but never
        // advertise it as a customer-facing choice for the electric comb.
        if (normalize(item.original) === 'white') {
          item.suppressedFacets = [...FACET_ORDER];
        } else if (normalize(item.original) === 'upgrade high electricity') {
          item.variant = 'Electric Comb';
          item.product = 'Electric Comb';
        }
      });
    }

    if (handle === DESHEDDING_MITT_HANDLE && model.position === 0) {
      model.items.forEach((item) => {
        if (!/^hair removal(?: gloves?)?$/.test(normalize(item.original))) return;
        // This supplier value is not a second product type: every option is a
        // hair-removal mitt. Present it as the same black mitt while retaining
        // its raw Shopify value, which owns the valid 6 PCS and 10 PCS variants.
        item.color = 'Black';
        item.variant = '';
        item.product = '';
        item.suppressedFacets = ['variant'];
      });
    }
  };

  const readVariants = (root) => {
    try {
      const variants = JSON.parse(q(root, '[data-fv3-variants]')?.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (_error) {
      return [];
    }
  };

  const globallyAvailable = (model, item, variants) => Boolean(item && variants.some((variant) => (
    variant.available && variant.options[model.position] === item.original
  )));

  const variantForItem = (model, item, variants, availableOnly = false) => {
    if (!item) return null;
    return variants.find((variant) => (
      (!availableOnly || variant.available) && variant.options[model.position] === item.original
    )) || null;
  };

  const checkedItem = (model) => model?.items.find((item) => item.radio.checked) || model?.items[0] || null;

  const resolveFacetItem = (items, state, facet, value, available = () => true) => {
    // Compatibility: release v2.65 tests used `pack`/`product`; the storefront
    // now calls those same truthful concepts QUANTITY/VARIANT.
    const facetKey = facet === 'pack' ? 'pack' : facet === 'product' ? 'product' : facet;
    const candidates = items.filter((item) => item[facetKey] === value && available(item));
    if (!candidates.length) return null;
    const otherFacets = FACET_ORDER.filter((candidateFacet) => candidateFacet !== facetKey);
    return candidates.reduce((best, item) => {
      let score = otherFacets.reduce((total, candidateFacet) => {
        if (!state?.[candidateFacet]) return total;
        return total + (item[candidateFacet] === state[candidateFacet] ? 6 : 0);
      }, item.original === state?.original ? 2 : 0);
      if (!state?.quantity && !state?.pack
        && (facetKey === 'variant' || facetKey === 'color' || facetKey === 'product')
        && (item.quantity === '1 PC' || item.pack === '1 Piece')) score += 2;
      if (!best || score > best.score) return { item, score };
      return best;
    }, null)?.item || candidates[0];
  };

  const imageFromThumb = (thumb) => {
    const image = q(thumb, 'img');
    return image?.currentSrc || image?.src || thumb?.dataset.image || '';
  };

  const imageForVariant = (root, variant) => {
    if (variant?.optionImage) return variant.optionImage;
    const galleryKey = String(variant?.galleryKey || '').trim();
    if (!galleryKey || galleryKey === 'all') return '';
    const thumb = qa(root, '[data-fv4-thumb]').find((candidate) => {
      const groups = String(candidate.dataset.mediaGroups || '').split(/\s+/).filter(Boolean);
      const colors = String(candidate.dataset.variantColors || '');
      return groups.includes(galleryKey) || colors.includes(`|${galleryKey}|`);
    });
    return imageFromThumb(thumb);
  };

  const appendVisual = (button, root, model, item, facet, label, variants) => {
    if (facet === 'size' || facet === 'quantity') return false;
    const variant = variantForItem(model, item, variants, true) || variantForItem(model, item, variants);
    const source = imageForVariant(root, variant);
    const token = facet === 'color' ? colorTokenFor(item?.color || label) : '';
    const swatch = token ? colorMap[token] : '';
    if (!source && !swatch) return false;
    const visual = document.createElement('span');
    visual.className = 'fv6-option-button__thumb';
    visual.setAttribute('aria-hidden', 'true');
    if (source) {
      const image = document.createElement('img');
      image.src = source;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 72;
      image.height = 72;
      visual.appendChild(image);
    } else {
      visual.classList.add('fv6-option-button__thumb--swatch');
      visual.style.background = swatch;
    }
    if (source && swatch) {
      const dot = document.createElement('i');
      dot.className = 'fv6-option-button__color-dot';
      dot.style.background = swatch;
      dot.setAttribute('aria-hidden', 'true');
      visual.appendChild(dot);
    }
    button.appendChild(visual);
    return true;
  };

  const isSupplierOnlyGroup = (root, group, radios) => {
    const label = normalize(group.dataset.optionName || q(group, 'legend')?.textContent || '');
    const values = radios.map((radio) => normalize(radio.value));
    if (/\b(item no|item number|sku|supplier code|ships from)\b/.test(label)) return true;
    const isFish = root.dataset.productHandle === 'simulation-electric-funny-cat-simulation-fish';
    if (isFish && values.length && values.every((value) => value === '30cm' || value === '1pc')) return true;
    return values.length === 1 && (values[0] === 'default title' || /^[a-z]{1,4}\d{2,}$/i.test(values[0]));
  };

  const buildModels = (root) => qa(root, '[data-fv3-option-group]')
    .map((group) => {
      const radios = qa(group, 'input[data-fv3-option]');
      if (!radios.length) return null;
      if (isSupplierOnlyGroup(root, group, radios)) {
        // Keep the checked native inputs available to Shopify's variant form,
        // but never expose supplier metadata as a customer-facing choice.
        group.classList.add('fv6-native-option-source');
        group.setAttribute('aria-hidden', 'true');
        return null;
      }
      if (radios.length < 2) return null;
      const role = inferGroupRole(group, radios);
      const colorMode = shouldExtractColors(radios, role);
      const quantityMode = radios.some((radio) => packInfo(radio.value).label);
      const model = {
        group, radios, role,
        position: Math.max(0, Number(group.dataset.optionPosition || 1) - 1)
      };
      model.items = radios.map((radio) => parseItem(radio, role, colorMode, quantityMode));
      applyProductSemantics(root, model);
      return model;
    })
    .filter(Boolean);

  const facetValues = (model, facet) => uniq(model.items
    .filter((item) => !item.suppressedFacets?.includes(facet))
    .map((item) => item[facet]));

  const shouldShowFacet = (root, model, facet) => {
    const values = facetValues(model, facet);
    if (!values.length) return false;
    if (root.dataset.productHandle === ELECTRIC_LICE_COMB_HANDLE && facet === 'variant') return true;
    if (values.length > 1) return true;
    const populatedFacets = FACET_ORDER.filter((candidate) => facetValues(model, candidate).length).length;
    return populatedFacets > 1 && (facet === 'color' || facet === 'variant');
  };

  const resolveFacetChoice = (model, facet, value, variants, availableOnly = true) => resolveFacetItem(
    model.items,
    checkedItem(model),
    facet,
    value,
    availableOnly ? (item) => globallyAvailable(model, item, variants) : () => true
  );

  const createButton = (root, model, facet, value, variants) => {
    const item = resolveFacetChoice(model, facet, value, variants, false);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `fv6-option-button fv6-option-button--${facet}`;
    button.dataset.fv6Facet = facet;
    button.dataset.fv6Position = String(model.position);
    button.dataset.value = value;
    button.setAttribute('aria-label', `${FACET_HEADINGS[facet]}: ${value}`);
    button.setAttribute('aria-pressed', 'false');
    if (facet !== 'size') {
      const hasVisual = appendVisual(button, root, model, item, facet, value, variants);
      if (!hasVisual) button.classList.add('fv6-option-button--text');
    }
    const copy = document.createElement('span');
    copy.className = 'fv6-option-button__copy';
    const title = document.createElement('strong');
    title.textContent = value;
    copy.appendChild(title);
    button.appendChild(copy);
    return button;
  };

  const createFacetSection = (root, model, facet, variants) => {
    const values = facetValues(model, facet);
    const section = document.createElement('section');
    section.className = `fv6-option-subgroup fv6-option-subgroup--${facet}`;
    section.dataset.fv6FacetSection = facet;
    section.dataset.fv6Position = String(model.position);
    const heading = document.createElement('div');
    heading.className = 'fv6-option-subgroup__heading';
    const title = document.createElement('strong');
    title.textContent = FACET_HEADINGS[facet];
    const selected = document.createElement('span');
    selected.dataset.fv6Selected = '';
    selected.setAttribute('aria-live', 'polite');
    heading.append(title, selected);
    const rail = document.createElement('div');
    rail.className = 'fv6-option-buttons';
    rail.setAttribute('role', 'group');
    rail.setAttribute('aria-label', FACET_HEADINGS[facet]);
    rail.tabIndex = -1;
    values.forEach((value) => rail.appendChild(createButton(root, model, facet, value, variants)));
    section.append(heading, rail);
    return section;
  };

  const syncHub = (root, hub, models, variants) => {
    qa(hub, '[data-fv6-facet-section]').forEach((section) => {
      const position = Number(section.dataset.fv6Position || 0);
      const facet = section.dataset.fv6FacetSection;
      const model = models.find((candidate) => candidate.position === position);
      const state = checkedItem(model);
      const selected = q(section, '[data-fv6-selected]');
      if (selected) selected.textContent = state?.[facet] || '';
      qa(section, '.fv6-option-button').forEach((button) => {
        const value = button.dataset.value;
        const availableItem = resolveFacetChoice(model, facet, value, variants, true);
        const displayItem = availableItem || resolveFacetChoice(model, facet, value, variants, false);
        const active = state?.[facet] === value;
        const available = Boolean(availableItem);
        button.classList.toggle('is-active', active);
        button.classList.toggle('is-unavailable', !available);
        button.setAttribute('aria-pressed', String(active));
        button.disabled = !available;
        const image = q(button, '.fv6-option-button__thumb img');
        if (image && displayItem) {
          const variant = variantForItem(model, displayItem, variants, true)
            || variantForItem(model, displayItem, variants);
          const source = imageForVariant(root, variant);
          if (source && image.getAttribute('src') !== source) image.src = source;
        }
      });
    });
  };

  const itemForVariant = (model, variant) => model?.items.find((item) => (
    item.original === variant?.options?.[model.position]
  )) || null;

  const variantForFacetSelection = (models, selectedModel, facet, value, variants) => {
    const current = new Map(models.map((model) => [model.position, checkedItem(model)]));
    const candidates = variants.filter((variant) => {
      if (!variant.available) return false;
      return itemForVariant(selectedModel, variant)?.[facet] === value;
    });
    if (!candidates.length) return null;
    return candidates.reduce((best, variant) => {
      const score = models.reduce((total, model) => {
        const candidateItem = itemForVariant(model, variant);
        const currentItem = current.get(model.position);
        if (!candidateItem || !currentItem) return total;
        if (candidateItem.original === currentItem.original) total += 12;
        FACET_ORDER.forEach((candidateFacet) => {
          if (model === selectedModel && candidateFacet === facet) return;
          if (currentItem[candidateFacet] && candidateItem[candidateFacet] === currentItem[candidateFacet]) total += 4;
        });
        return total;
      }, 0);
      if (!best || score > best.score) return { variant, score };
      return best;
    }, null)?.variant || candidates[0];
  };

  const selectFacet = (root, hub, models, variants, button) => {
    const position = Number(button.dataset.fv6Position || 0);
    const facet = button.dataset.fv6Facet;
    const model = models.find((candidate) => candidate.position === position);
    const item = resolveFacetChoice(model, facet, button.dataset.value, variants, true);
    if (!model || !item || !globallyAvailable(model, item, variants)) return;
    const targetVariant = variantForFacetSelection(models, model, facet, button.dataset.value, variants);
    const targetItem = itemForVariant(model, targetVariant) || item;
    if (targetVariant) {
      models.forEach((candidateModel) => {
        const selectedValue = targetVariant.options[candidateModel.position];
        candidateModel.radios.forEach((radio) => { radio.checked = radio.value === selectedValue; });
      });
    } else {
      model.radios.forEach((radio) => { radio.checked = radio === targetItem.radio; });
    }
    targetItem.radio.dispatchEvent(new Event('change', { bubbles: true }));
    window.setTimeout(() => syncHub(root, hub, models, variants), 0);
  };

  const enhanceProductOptions = (root) => {
    if (root.dataset.fv66OptionsReady === 'true') return;
    const options = q(root, '.fv3-options');
    const variants = readVariants(root);
    if (!options || !variants.length) return;
    const models = buildModels(root);
    if (!models.length) return;
    const hub = document.createElement('div');
    hub.className = 'fv6-option-hub';
    hub.dataset.fv6OptionHub = '';
    hub.setAttribute('aria-label', 'Product options');
    FACET_ORDER.forEach((facet) => {
      models
        .filter((model) => shouldShowFacet(root, model, facet))
        .sort((left, right) => left.position - right.position)
        .forEach((model) => hub.appendChild(createFacetSection(root, model, facet, variants)));
    });
    if (!hub.children.length) return;
    options.prepend(hub);
    options.classList.add('fv6-options-ready');
    models.forEach((model) => {
      model.group.classList.add('fv6-native-option-source');
      model.group.setAttribute('aria-hidden', 'true');
    });
    hub.addEventListener('click', (event) => {
      const button = event.target.closest('.fv6-option-button');
      if (!button || button.disabled || !hub.contains(button)) return;
      selectFacet(root, hub, models, variants, button);
    });
    hub.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const button = event.target.closest('.fv6-option-button');
      const rail = button?.closest('.fv6-option-buttons');
      if (!button || !rail) return;
      const enabled = qa(rail, '.fv6-option-button:not(:disabled)');
      const index = enabled.indexOf(button);
      if (index < 0 || !enabled.length) return;
      event.preventDefault();
      const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length;
      enabled[nextIndex].focus({ preventScroll: true });
      enabled[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
    root.addEventListener('change', (event) => {
      if (!event.target.matches('input[data-fv3-option]')) return;
      window.setTimeout(() => syncHub(root, hub, models, variants), 0);
    });
    root.dataset.fv66OptionsReady = 'true';
    syncHub(root, hub, models, variants);
  };

  const init = () => {
    qa(document, '[data-fv3-product]').forEach(enhanceProductOptions);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', (event) => {
    qa(event.target, '[data-fv3-product]').forEach(enhanceProductOptions);
  });
})();
